/**
 * P-270 · what each column of a Tasks list holds, in one place.
 *
 * The user's report was *"add column names to specify what each column contains — in My Tasks the
 * members column is 'Assigned by', right?"* (2026-09-15). It is: the person a row draws is the one
 * on the **other** side of the delegation (`TaskRow`), so the same avatar means *who gave me this*
 * on **My Tasks** and *who I gave it to* on **Tasks Assigned**. A list that never says which is
 * which is the defect, and one label per tab is the fix.
 *
 * The header is not a grid: the rows are `<div>`s laid out with flex, not a `<table>`, so this
 * file carries no widths. Every column's width is a CSS custom property on `.ez-tasks`
 * (`--ez-tasks-*-w` in `tasks.css`), which both the header cell and the row cell read — alignment
 * by construction rather than by two numbers that have to be kept equal by hand.
 *
 * Sorting: a header is clickable **only where the sort menu already offers that sort**. The page
 * has no `name_desc` and no per-board sort, so Board, the person and Status are labels, not
 * buttons — a header that sorted by something the menu cannot express would be a second,
 * disagreeing sort model.
 */
import type { TaskSort, TaskTab } from './types';

export const TASK_COLUMN_KEYS = ['item', 'board', 'person', 'status', 'due', 'assigned'] as const;
export type TaskColumnKey = (typeof TASK_COLUMN_KEYS)[number];

export interface TaskColumnDef {
  key: TaskColumnKey;
  label: string;
  /**
   * The sorts the toolbar's menu already offers for this column, in the order a click cycles
   * them. Absent when the menu offers none — the header is then plain text.
   */
  sorts?: readonly TaskSort[];
}

/**
 * The person column's label, per tab — the one string the user actually asked for.
 *
 * **Archive** carries both, and that is a deliberate reading of the card: an archived row is one
 * that was assigned *to me* (`services/tasks/queries.ts` filters the tab by assignee), and it may
 * equally be a task I gave myself, so the column can hold either side of the delegation. The
 * combined label says so instead of picking one and being wrong half the time.
 */
export const TASK_PERSON_COLUMN_LABELS: Record<TaskTab, string> = {
  mine: 'Assigned by',
  assigned: 'Assigned to',
  archive: 'Assigned by / Assigned to',
};

/** The six columns a list row draws, in the order the row draws them. */
export function taskColumns(tab: TaskTab): TaskColumnDef[] {
  return [
    { key: 'item', label: 'Item', sorts: ['name_asc'] },
    { key: 'board', label: 'Board' },
    { key: 'person', label: TASK_PERSON_COLUMN_LABELS[tab] },
    { key: 'status', label: 'Status' },
    { key: 'due', label: 'Due', sorts: ['due_asc', 'due_desc'] },
    { key: 'assigned', label: 'Assigned on', sorts: ['assigned_desc', 'assigned_asc'] },
  ];
}

/** Every sort the page has, split by direction, so a header can draw the right arrow. */
const SORT_DIRECTION: Record<TaskSort, 'ascending' | 'descending'> = {
  due_asc: 'ascending',
  due_desc: 'descending',
  assigned_asc: 'ascending',
  assigned_desc: 'descending',
  name_asc: 'ascending',
};

/** `aria-sort` for one header: `none` unless the list is currently sorted by this column. */
export function taskColumnSortState(column: TaskColumnDef, sort: TaskSort): 'ascending' | 'descending' | 'none' {
  if (!column.sorts || !column.sorts.includes(sort)) return 'none';
  return SORT_DIRECTION[sort];
}

/**
 * What a click on this header selects.
 *
 * The first click takes the column's first sort — which is the direction the menu lists first, so
 * Due opens soonest-first and Assigned on opens newest-first. A click on the column that is
 * already sorted moves to the next direction it offers, and wraps; a column with one direction
 * (Name, which has no `name_desc`) simply stays there.
 */
export function nextTaskSort(column: TaskColumnDef, sort: TaskSort): TaskSort | null {
  if (!column.sorts || column.sorts.length === 0) return null;
  const at = column.sorts.indexOf(sort);
  return at === -1 ? column.sorts[0] : column.sorts[(at + 1) % column.sorts.length];
}

/**
 * The phone's replacement for the header (`tasks/P-270`): one line under the tab strip saying
 * what the wordless things in a card row mean.
 *
 * Phones get cards, not columns — a 390 px row cannot carry six headers — so the row's avatar,
 * board chip and status pill carry accessible names (`TaskRow`) and this sentence carries the
 * same mapping for everybody who is looking rather than listening. It is deliberately short:
 * measured at 12 px it has to stay on **one** line inside 390 px.
 */
export function taskLegend(tab: TaskTab): string {
  return `Avatar = ${
    tab === 'mine' ? 'assigned by' : tab === 'assigned' ? 'assigned to' : 'assigned by / to'
  } · chip = board · pill = status`;
}
